import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { api } from '@/lib/supabase/client';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Filter, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tradesperson } from '@/lib/types';
import { useAuth } from '@/contexts/AuthContext';
import { default as TradesPurpleLayout } from '../components/Layout';
import { TradeList } from '../components/TradeList';
import { SmartQuoteWrapper } from '../components/SmartQuote';
import { debug } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ActionButtons, ActionButtonType } from '../components/ActionButtons';
import { ActionPanels } from '../components/ActionPanels';
import { UtilityRow } from '../components/UtilityRow';
import { formatPostcodeDisplay, validatePostcode } from '@/lib/postcodeUtils';

/**
 * ResultsPage component - simplified version with unified TradeList component
 */
export default function ResultsPage() {
  // Get parameters directly from React Router
  const params = useParams();
  const postcodeOrUsername = params.postcodeOrUsername;
  const tradeType = params.tradeType;
  const tradespersonId = params.tradespersonId;

  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on tradespurple.com domain
  const isTpDomain = typeof window !== 'undefined' &&
                    window.location.hostname.includes('tradespurple.com');
  const homeRoute = isTpDomain ? '/' : '/tradespurple';

  // Get authentication state
  const { isAuthenticated } = useAuth();

  // Special state for highlighted tradesperson (when shared)
  const [highlightedTradesperson, setHighlightedTradesperson] = useState<Tradesperson | null>(null);

  // Log parameters in development mode only
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      debug.log('[ResultsPage] Parameters:', {
        postcodeOrUsername,
        tradeType,
        location: location.pathname
      });
    }
  }, [postcodeOrUsername, tradeType, location]);

  // UI state for showing/hiding features
  const [showTradesList, setShowTradesList] = useState(true);
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [jobDescription, setJobDescription] = useState('');
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [filteredTradesCount, setFilteredTradesCount] = useState(0);

  // Store filtered tradespeople for sharing with SmartQuote
  const [filteredTradespeople, setFilteredTradespeople] = useState<Tradesperson[]>([]);

  // Action button/panel state for the reused components
  const [activeButton, setActiveButton] = useState<ActionButtonType | null>(null);

  // Define stable callbacks - IMPORTANT: these must be at component level
  const handleTradespersonSaved = useCallback(() => {
    // This is a callback for when a tradesperson is saved
    debug.log('[ResultsPage] Tradesperson saved, refreshing list');
  }, []);

  const handleDataLoaded = useCallback((trades: Tradesperson[]) => {
    // Update the trades count when data is loaded
    setFilteredTradesCount(trades.length);

    // Only update filteredTradespeople if the data actually changed
    if (trades.length === 0 && filteredTradespeople.length === 0) {
      // Don't update state if both are empty arrays
      return;
    }

    // Simple check to avoid unnecessary updates
    // Fallback to the entire object if id is undefined
    const tradesChanged = JSON.stringify(trades.map(t => t.id || t)) !==
                        JSON.stringify(filteredTradespeople.map(t => t.id || t));

    if (tradesChanged) {
      debug.log('[ResultsPage] Filtered tradespeople updated', {
        count: trades.length,
        changed: true
      });
      setFilteredTradespeople(trades);
    } else {
      debug.log('[ResultsPage] Skipping update (data unchanged)', {
        count: trades.length,
        changed: false
      });
    }
  }, [filteredTradespeople]);

  // If no postcode/username or userId is provided, redirect to the landing page
  useEffect(() => {
    if (!postcodeOrUsername && !userId) {
      navigate(homeRoute);
    }
  }, [postcodeOrUsername, userId, navigate, homeRoute]);

  // International postcode validation - used to determine if the parameter is a postcode or username
  const isPostcode = (value: string) => {
    return validatePostcode(value);
  };

  // Determine the proper data source based on URL params
  const getDataSource = () => {
    if (postcodeOrUsername) {
      return isPostcode(postcodeOrUsername) ? 'postcode' : 'username';
    } else if (userId) {
      return 'user';
    }
    return 'popular';
  };

  // Load highlighted tradesperson if ID is in the URL
  useEffect(() => {
    if (!tradespersonId) return;

    async function loadHighlightedTradesperson() {
      try {
        // Worker API by-id lookup (old Supabase query is dead post-D1-migration).
        const { tradesperson: data } = await api.get<{ tradesperson: Tradesperson | null }>(
          `/tradespeople/${encodeURIComponent(tradespersonId)}`
        );

        if (data) {
          setHighlightedTradesperson(data as Tradesperson);
        }
      } catch (error) {
        debug.error('[ResultsPage] Failed to load highlighted tradesperson:', error);
      }
    }

    loadHighlightedTradesperson();
  }, [tradespersonId]);

  // Handle category filter changes
  const handleCategoryChange = (category: string | null) => {
    setActiveFilter(category);
  };

  // Handle button clicks from ActionButtons component
  const handleActionButtonClick = (button: ActionButtonType) => {
    // Only allow SmartQuote button to be active
    if (button === 'quote') {
      setActiveButton(button);
      setShowTradesList(false);
    }
  };

  // Handle filter toggle from UtilityRow component
  const handleFilterToggle = () => {
    setShowCategoryFilter(!showCategoryFilter);
  };

  // Handle closing the action panel
  const handleClosePanel = () => {
    setActiveButton(null);
    setShowTradesList(true);
  };

  // Handle data change notification from action panel
  const handleDataChange = () => {
    // Refresh the trades list
    debug.log('[ResultsPage] Data changed, refreshing list');
  };

  // Generate title for the location header
  const getLocationTitle = () => {
    if (postcodeOrUsername) {
      if (isPostcode(postcodeOrUsername)) {
        return activeFilter
          ? `${activeFilter} near ${formatPostcodeDisplay(postcodeOrUsername)}`
          : `Tradespeople near ${formatPostcodeDisplay(postcodeOrUsername)}`;
      } else {
        return activeFilter
          ? `${postcodeOrUsername}'s recommended ${activeFilter}`
          : `${postcodeOrUsername}'s recommended tradespeople`;
      }
    } else if (userId) {
      return activeFilter
        ? `Shared ${activeFilter}`
        : "Shared tradespeople";
    } else {
      return activeFilter
        ? `Popular ${activeFilter}`
        : "Popular tradespeople";
    }
  };

  return (
    <TradesPurpleLayout>
      <Helmet>
        <title>TradesPurple | {getLocationTitle()}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover" />
        <meta name="theme-color" content="#7e22ce" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      </Helmet>

      {/* Combined location header with back button */}
      <div className="mb-2 mt-0.5 px-4">
        <div className="bg-white p-1.5 rounded-lg text-purple-900 flex items-center justify-between border-2 border-purple-900 shadow-md">
          <div className="flex items-center">
            <div className="w-6 h-6 rounded-lg bg-purple-700 flex items-center justify-center mr-1.5">
              <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
              </svg>
            </div>
            <span className="font-medium text-sm">{getLocationTitle()}</span>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(homeRoute)}
            className="rounded-full hover:bg-purple-100 flex items-center p-0.5 h-7 w-7"
          >
            <ArrowLeft className="h-4 w-4 text-purple-900" />
          </Button>
        </div>
      </div>

      {/* Action Buttons and Panels */}
      {filteredTradesCount > 0 && (
        <>
          {/* Action Panels - shown when an action button is clicked */}
          <ActionPanels
            activePanel={activeButton}
            onClose={handleClosePanel}
            onDataChange={handleDataChange}
            filteredTradespeople={filteredTradespeople}
          />

          {/* Only show custom button row when no panel is active */}
          {(activeButton !== 'quote' && activeButton !== 'add') && (
            <div className="mb-2 px-4">
              {/* SmartQuote Button - full width */}
              <div className="mb-3">
                <button
                  onClick={() => handleActionButtonClick('quote')}
                  aria-label="Request quotes with SmartQuote"
                  className={`
                    flex items-center justify-center rounded-xl transition-colors px-4 py-2.5 border-2 w-full
                    ${activeButton === 'quote'
                      ? "bg-purple-100 text-purple-900 border-purple-300"
                      : "bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
                    }
                  `}
                  disabled={filteredTradespeople.length === 0}
                >
                  <MessageSquare className="h-5 w-5 mr-2 flex-shrink-0" />
                  <span className="text-base font-semibold">SmartQuote</span>
                </button>
              </div>

              {/* Divider */}
              <div className="border-t border-purple-100 mb-1.5"></div>

              {/* Filter Button row */}
              <div className="mb-1">
                {/* Filter Button - left aligned */}
                <button
                  onClick={handleFilterToggle}
                  aria-label="Filter trades"
                  className={`
                    flex items-center justify-center rounded-lg transition-colors px-3 py-1.5 border-2
                    ${showCategoryFilter
                      ? "bg-purple-100 text-purple-900 border-purple-300"
                      : "bg-white hover:bg-purple-50 text-purple-800 border-purple-200"
                    }
                  `}
                >
                  <Filter className="h-4 w-4 mr-1.5" />
                  <span className="text-sm font-medium">Filter</span>
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* TradeList - only shown when quote or add panels are not active */}
      {(activeButton !== 'quote' && activeButton !== 'add') && (
        <TradeList
          mode="results"
          dataSource={getDataSource()}
          postcodeOrUsername={postcodeOrUsername}
          userId={userId || undefined}
          initialLimit={10}
          categoryFilter={activeFilter}
          onCategoryChange={handleCategoryChange}
          showTitle={false}
          showShareButton={true}
          showCategoryFilter={showCategoryFilter}
          highlightedTradesperson={highlightedTradesperson}
          syncWithUrl={true}
          onTradespersonSaved={handleTradespersonSaved}
          onDataLoaded={handleDataLoaded}
        />
      )}
    </TradesPurpleLayout>
  );
}
